import { useOverflow } from '@components/ResourceLink/ResourceLink.hooks';
import { TagDialogHoverProps } from '@components/TagDialog/TagDialogHover';
import { GroupProps } from '@mantine/core';
import { useMergedRef, useResizeObserver } from '@mantine/hooks';
import { ITag, MaybeUndef } from '@models/Tag.model';
import { TagWithDelete } from '@pages/Asset/components/Sidebar/components/Tags/TagWithDelete';
import { forwardRef, useRef, memo } from 'react';
import { TagGroup } from './styles';

type TagData = Pick<ITag, 'id' | 'name'> & {
  tagCategory: MaybeUndef<Pick<NonNullable<ITag['tagCategory']>, 'color'>>;
};

export interface TagListWithDeleteT<T extends any[]>
  extends Pick<
    TagDialogHoverProps<T>,
    'deleteHandler' | 'deleteHandlerArgs' | 'calcTagProps'
  > {
  tags: TagData[];
}

function FWTagListWithDelete<T extends any[]>(
  props: TagListWithDeleteT<T> &
    React.HTMLAttributes<HTMLDivElement> &
    GroupProps,
  ref: React.ForwardedRef<HTMLDivElement | null>,
) {
  const {
    tags,
    deleteHandler = () => () => {},
    // Both must be specified.
    deleteHandlerArgs = [] as unknown as T,
    calcTagProps = () => {},
    ...rest
  } = props;
  const tagListRef = useRef<HTMLDivElement>(null);
  // Todo: Optimise resize checks
  const [r4, el] = useResizeObserver();
  const hTagSer = String(el?.width).concat(String(el?.height));
  const isHeadOverflow = useOverflow(tagListRef, hTagSer);
  const rrefs = useMergedRef(ref, tagListRef, r4);
  return (
    <TagGroup
      data-overflow={isHeadOverflow}
      noWrap
      spacing={8}
      ref={rrefs}
      {...rest}
    >
      {tags.map(tag => {
        return (
          <TagWithDelete
            key={tag.id}
            name={tag.name}
            color={tag?.tagCategory?.color}
            deleted={false}
            className="rm-m"
            onDeleteIconClick={deleteHandler(tag, ...deleteHandlerArgs)}
            {...calcTagProps(tag)}
          />
        );
      })}
    </TagGroup>
  );
}
export const TagListWithDelete = memo(forwardRef(FWTagListWithDelete));
